import React, { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff, AlertCircle, CheckCircle, Settings, User, Camera, Trash2 } from 'lucide-react';
import { AppShell } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, API_KEY } from '../config';

const AVATAR_KEY = 'user_avatar';

function getStoredAvatar() {
  try { return localStorage.getItem(AVATAR_KEY) || null; } catch { return null; }
}
function saveAvatar(dataUrl) {
  try { localStorage.setItem(AVATAR_KEY, dataUrl); } catch { }
}
function removeAvatar() {
  try { localStorage.removeItem(AVATAR_KEY); } catch { }
}

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

export default function AccountScreen() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [name, setName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [avatar, setAvatar] = useState(getStoredAvatar);
  const [avatarHover, setAvatarHover] = useState(false);
  const fileInputRef = useRef(null);

  const username = user?.username || localStorage.getItem('username') || '';
  const initials = username.slice(0, 2).toUpperCase();

  const capitalizeName = (text) =>
    text.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select a valid image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5MB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setAvatar(dataUrl);
      saveAvatar(dataUrl);
      setSuccess('Profile photo updated.');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemoveAvatar = () => {
    setAvatar(null);
    removeAvatar();
    setSuccess('Profile photo removed.');
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!name.trim() || !newPassword.trim() || !repeatPassword.trim()) { setError('All fields are required.'); return; }
    if (newPassword !== repeatPassword) { setError('Passwords do not match.'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify({ username, name: name.trim(), password: newPassword.trim() }),
      });
      const data = await res.json();
      if (res.ok) { setSuccess(data.message || 'Profile updated.'); setNewPassword(''); setRepeatPassword(''); }
      else setError(data.message || 'Update failed.');
    } catch { setError('Cannot connect to server.'); }
    finally { setLoading(false); }
  };

  const ProfileCard = (
    <div className="card">
      <div className="card-header" style={{ marginBottom: 20 }}>
        <div className="card-title">
          <div className="card-icon" style={{ background: 'rgba(5,150,105,0.1)', color: 'var(--teal)' }}>
            <User size={16} />
          </div>
          Your Account
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div
          style={{ position: 'relative', cursor: 'pointer' }}
          onMouseEnter={() => setAvatarHover(true)}
          onMouseLeave={() => setAvatarHover(false)}
          onClick={() => fileInputRef.current?.click()}
        >
          <div style={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            background: avatar ? 'transparent' : 'linear-gradient(135deg, var(--blue-primary), var(--cyan))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.6rem',
            fontWeight: 700,
            color: '#fff',
            overflow: 'hidden',
            border: '3px solid',
            boxShadow: 'var(--shadow-sm)',
            transition: 'border-color 0.2s',
            borderColor: avatarHover ? 'var(--blue-primary)' : 'var(--border-light)',
          }}>
            {avatar
              ? <img src={avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : initials
            }
          </div>
          <div style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: 'rgba(10,22,40,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: avatarHover ? 1 : 0,
            transition: 'opacity 0.2s',
          }}>
            <Camera size={20} color="#fff" />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAvatarChange}
          />
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '1rem' }}>{username}</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {user?.email || 'Medical Staff'}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button type="button" className="btn btn-sm btn-outline" onClick={() => fileInputRef.current?.click()}>
            <Camera size={13} />
            {avatar ? 'Change Photo' : 'Upload Photo'}
          </button>
          {avatar && (
            <button type="button" className="btn btn-sm btn-ghost" style={{ color: 'var(--error)' }} onClick={handleRemoveAvatar}>
              <Trash2 size={13} />
              Remove
            </button>
          )}
        </div>

        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', margin: 0 }}>
          JPG, PNG or GIF · Max 5MB
        </p>
      </div>
    </div>
  );

  const RequirementsCard = (
    <div className="card">
      <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 10, fontSize: '0.875rem' }}>
        Password Requirements
      </div>
      {['Minimum 8 characters', 'Mix of letters and numbers recommended', 'Avoid reusing old passwords'].map(r => (
        <div key={r} style={{ display: 'flex', gap: 8, fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 6 }}>
          <span style={{ color: 'var(--teal)' }}>✓</span>{r}
        </div>
      ))}
    </div>
  );

  return (
    <AppShell title="Account Settings">
      <div className="page-header">
        <h1 className="page-title">Account Settings</h1>
        <p className="page-subtitle">Manage your profile and credentials</p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 320px',
        gap: 24,
        alignItems: 'start',
      }}>
        {/* On mobile: profile card first, then form. On desktop: form left, cards right */}
        {isMobile && ProfileCard}

        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <div className="card-icon"><Settings size={16} /></div>
              Edit Profile
            </div>
          </div>

          {error && <div className="alert alert-error"><AlertCircle size={16} />{error}</div>}
          {success && <div className="alert alert-success"><CheckCircle size={16} />{success}</div>}

          <form onSubmit={handleUpdate}>
            <div className="form-group">
              <label className="form-label">
                Username{' '}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', fontSize: '0.75rem' }}>
                  (cannot be changed)
                </span>
              </label>
              <input className="form-control" type="text" value={username} disabled />
            </div>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                className="form-control"
                type="text"
                placeholder="Dr. Jane Smith"
                value={name}
                onChange={e => setName(capitalizeName(e.target.value))}
              />
            </div>
            <hr className="divider" />
            <div className="form-group">
              <label className="form-label">New Password</label>
              <div className="input-wrap">
                <input
                  className="form-control"
                  type={showPw1 ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  style={{ paddingRight: 44 }}
                />
                <button type="button" className="input-icon" onClick={() => setShowPw1(!showPw1)} tabIndex={-1}>
                  {showPw1 ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <div className="input-wrap">
                <input
                  className="form-control"
                  type={showPw2 ? 'text' : 'password'}
                  placeholder="Repeat new password"
                  value={repeatPassword}
                  onChange={e => setRepeatPassword(e.target.value)}
                  style={{ paddingRight: 44 }}
                />
                <button type="button" className="input-icon" onClick={() => setShowPw2(!showPw2)} tabIndex={-1}>
                  {showPw2 ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Save Changes'}
            </button>
          </form>
        </div>

        {/* On desktop: right column with both cards. On mobile: requirements below form */}
        {!isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {ProfileCard}
            {RequirementsCard}
          </div>
        ) : (
          RequirementsCard
        )}
      </div>
    </AppShell>
  );
}